import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { FindingHint } from "../../findings/FindingHint";
import { formatMetric } from "../../format/formatMetric";
import type { AnalysisMetric } from "../../model/Analysis";
import type { FindingSeverity } from "../../model/Finding";
import { metricComparison } from "../../model/metricComparison";
import { METRIC_HINTS } from "../../model/METRIC_HINTS";
import { METRIC_LABELS } from "../../model/METRIC_LABELS";
import { SEVERITY_LABELS } from "../../model/SEVERITY_LABELS";

const TYPICAL = msg({ id: "analysis.metric.typical", message: "typical {value}" });
const HINT_LABEL = msg({ id: "finding.metric.hint", message: "More about: {metric}" });

const DOT_COLORS: Record<FindingSeverity, string> = {
  good: "bg-good",
  warn: "bg-warn",
  bad: "bg-bad",
};

type MetricRowsProps = {
  metrics: readonly AnalysisMetric[];
};

// Метрики секции строками: значение игрока, типичное у сильных игроков на герое и сравнение
// словами («хуже, чем у 80 из 100»). Цвет точки — оценка метрики, обычное значение — серое.
// Вместо карточки на метрику: все цифры секции видны сразу и не спорят с ошибками.
export const MetricRows = ({ metrics }: MetricRowsProps) => {
  const { i18n } = useLingui();
  if (metrics.length === 0) {
    return null;
  }
  return (
    <ul className="flex flex-col">
      {metrics.map((metric) => {
        const label = i18n._(METRIC_LABELS[metric.metric]);
        const hint = METRIC_HINTS[metric.metric];
        const comparison = metricComparison(metric.severity, metric.percentile);
        return (
          <li
            key={metric.metric}
            data-metric={metric.metric}
            data-severity={metric.severity ?? "none"}
            className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t border-border py-2.5 text-sm first:border-t-0"
          >
            <span className="flex min-w-0 flex-[1_1_220px] items-center gap-2">
              <span
                aria-hidden="true"
                className={cn(
                  "size-2.5 shrink-0 rounded-full",
                  metric.severity === null ? "bg-subtle" : DOT_COLORS[metric.severity],
                )}
              />
              {metric.severity !== null && (
                <span className="sr-only">{i18n._(SEVERITY_LABELS[metric.severity])}: </span>
              )}
              <span className="text-foreground">{label}</span>
              <FindingHint
                label={i18n._({ ...HINT_LABEL, values: { metric: label } })}
                what={i18n._(hint.meaning)}
                why={i18n._(hint.why)}
                fix={
                  metric.severity === "warn" || metric.severity === "bad"
                    ? i18n._(hint.fix)
                    : undefined
                }
              />
            </span>
            <span className="flex flex-wrap items-baseline gap-x-2">
              <b className="font-mono font-semibold text-foreground">
                {formatMetric(metric.metric, metric.value, i18n)}
              </b>
              <span className="text-xs text-subtle">
                {i18n._({
                  ...TYPICAL,
                  values: { value: formatMetric(metric.metric, metric.typical, i18n) },
                })}
              </span>
            </span>
            <span className="ml-auto text-right text-xs text-muted">
              {i18n._({ ...comparison.descriptor, values: comparison.values })}
            </span>
          </li>
        );
      })}
    </ul>
  );
};
